import { useState, useEffect, useRef } from 'react';
import { useTestStore, type TestId } from '@/store/useTestStore';
import { t } from '@/i18n/translations';
import { TestCard } from '@/components/TestCard';
import { MetricBox } from '@/components/MetricBox';
import { fmtMs, fmtInt, now, clamp } from '@/utils/format';
import { useFpsMeter } from '@/hooks/useFpsMeter';
import { scoreFromFps, scoreFromTime } from '@/utils/scoring';

interface Props { autoRun: boolean; autoNonce: number; }

export function EventsTest({ autoRun, autoNonce }: Props) {
  const { language, reportResult, onAutoDone } = useTestStore();
  const [running, setRunning] = useState(false);
  const [formN, setFormN] = useState(140);
  const [rate, setRate] = useState(60);
  const [metrics, setMetrics] = useState({ fps: 0, eventsPerSec: 0, renders: 0, lastLagMs: 0 });
  const fps = useFpsMeter(running);
  const timerRef = useRef(0);
  const eventsRef = useRef(0);
  const rendersRef = useRef(0);
  const lastTick = useRef(0);
  const autoDoneRef = useRef(false);
  const [values, setValues] = useState<string[]>([]);

  useEffect(() => {
    const n = clamp(parseInt(String(formN), 10) || 140, 40, 400);
    setValues(new Array(n).fill(''));
  }, [formN]);

  useEffect(() => { if (running) rendersRef.current++; });

  function validate(s: string) {
    if (s.length === 0) return true;
    if (s.length > 28) return false;
    return /^[a-zA-Z0-9 _-]+$/.test(s);
  }

  function start() {
    autoDoneRef.current = false;
    setRunning(true);
    eventsRef.current = 0;
    rendersRef.current = 0;
    lastTick.current = now();
    const r = clamp(parseInt(String(rate), 10) || 60, 10, 240);
    setRate(r);

    timerRef.current = window.setInterval(() => {
      const t0 = now();
      const n = values.length || 140;
      const idx = (eventsRef.current * 17) % n;
      const v = 'x' + ((eventsRef.current * 2654435761) >>> 0).toString(16).slice(0, 6);
      setValues((prev) => {
        const copy = prev.slice();
        copy[idx] = validate(v) ? v : v.slice(0, 10);
        return copy;
      });
      eventsRef.current++;
      const dt = now() - t0;
      const elapsed = now();
      if (elapsed - lastTick.current >= 1000) {
        setMetrics({ eventsPerSec: eventsRef.current, renders: rendersRef.current, lastLagMs: dt, fps });
        eventsRef.current = 0;
        rendersRef.current = 0;
        lastTick.current = elapsed;
      }
    }, Math.max(4, Math.floor(1000 / r)));
  }

  function stop() {
    setRunning(false);
    clearInterval(timerRef.current);
    const sc = Math.round(0.7 * scoreFromFps(fps) + 0.3 * scoreFromTime(metrics.lastLagMs || 9999, 6, 14));
    const testId: TestId = 'tv-events';
    reportResult(testId, { score: sc, primary: `FPS ${fps.toFixed(1)} · ${fmtInt(metrics.eventsPerSec)}/s`, details: metrics });
    if (autoRun && !autoDoneRef.current) { autoDoneRef.current = true; onAutoDone(testId); }
  }

  function reset() { stop(); setFormN(140); setRate(60); setMetrics({ fps: 0, eventsPerSec: 0, renders: 0, lastLagMs: 0 }); }

  useEffect(() => {
    if (!autoRun) return;
    setFormN(140); setRate(60);
    start();
    const timer = setTimeout(() => stop(), 6000);
    return () => clearTimeout(timer);
  }, [autoRun, autoNonce]);

  const nShow = Math.min(values.length, 40);

  return (
    <TestCard title={t('tv.events.title', language)} description={t('tv.events.desc', language)}>
      <div className="flex flex-wrap gap-2.5 items-center">
        <button className="btn btn-primary" onClick={start} disabled={running || autoRun}>{t('btn.start', language)}</button>
        <button className="btn btn-danger" onClick={stop} disabled={!running || autoRun}>{t('btn.stop', language)}</button>
        <button className="btn" onClick={reset} disabled={autoRun}>{t('btn.reset', language)}</button>
        <div className="flex-1" />
        <div className="field">
          <div className="label">{t('label.inputs', language)}</div>
          <input value={formN} disabled={running || autoRun} onChange={(e) => setFormN(Number(e.target.value))} inputMode="numeric" className="input w-20" />
        </div>
        <div className="field">
          <div className="label">{t('label.eventsRate', language)}</div>
          <input value={rate} disabled={running || autoRun} onChange={(e) => setRate(Number(e.target.value))} inputMode="numeric" className="input w-20" />
        </div>
      </div>
      <div className="grid grid-cols-4 gap-2.5 mt-3 max-lg:grid-cols-2 max-sm:grid-cols-1">
        <MetricBox label={t('metric.fps', language)} value={running ? fps.toFixed(1) : '—'} />
        <MetricBox label={t('metric.eventsPerSec', language)} value={running ? fmtInt(metrics.eventsPerSec) : '—'} />
        <MetricBox label={t('metric.rendersPerSec', language)} value={running ? fmtInt(metrics.renders) : '—'} />
        <MetricBox label={t('metric.handlerLag', language)} value={running ? fmtMs(metrics.lastLagMs) : '—'} />
      </div>
      <div className="h-px bg-border my-3.5" />
      <div className="border border-border rounded-2xl bg-black/[.14] p-3">
        <div className="text-xs text-muted mb-2">Preview ({nShow} inputs):</div>
        <div className="grid grid-cols-2 gap-2 max-sm:grid-cols-1">
          {Array.from({ length: nShow }).map((_, i) => (
            <div key={i}>
              <div className="text-xs text-muted font-mono">Field {i + 1}</div>
              <input
                value={values[i] || ''}
                onChange={(e) => {
                  const v = e.target.value;
                  setValues((prev) => { const c = prev.slice(); c[i] = validate(v) ? v : v.slice(0, 28); return c; });
                }}
                className="input w-full"
              />
            </div>
          ))}
        </div>
      </div>
    </TestCard>
  );
}
